import React, { useMemo, useState } from 'react';
import { storeRequestId } from '../api';
import {
  MAX_SOURCE_CHARACTERS,
  SOURCE_WARNING_THRESHOLD,
  isSourceOverLimit,
  sourceUtf8ByteLength,
} from '../constants/limits';
import { getColumnLabelsForTm } from '../utils/languages';
import TmLanguageSelector from './TmLanguageSelector';

function TranslationForm({ onSubmit, loading, selectedTmId, availableTMs = [], onTmChange }) {
  const [source, setSource] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectedTm = useMemo(
    () => availableTMs.find((t) => t.tm_id === selectedTmId) || {},
    [availableTMs, selectedTmId],
  );
  const columnLabels = useMemo(() => getColumnLabelsForTm(selectedTm), [selectedTm]);

  const byteLength = sourceUtf8ByteLength(source);
  const isOverLimit = isSourceOverLimit(source);
  const isNearLimit = byteLength >= SOURCE_WARNING_THRESHOLD && !isOverLimit;

  const handleTextChange = (e) => {
    const text = e.target.value;
    if (text.length > MAX_SOURCE_CHARACTERS) return;
    if (isSourceOverLimit(text)) return;
    setSource(text);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!source.trim() || isOverLimit) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await onSubmit({
        source: source.trim(),
        sourceLanguage: selectedTm.sourceLanguage || 'en-US',
        targetLanguage: selectedTm.targetLanguage || 'es-US',
        category: selectedTm.category || 'general',
        program: selectedTm.program || 'domestic',
        tm_id: selectedTmId,
      });

      if (result.req_id) {
        storeRequestId(result.req_id);
      }

      setSource('');
    } catch (error) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-content">
          <div className="step-number">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </div>
          <div className="card-header-text">
            <h2>Translate Text by Sections</h2>
            <p>Translate content section by section</p>
          </div>
          <div className="card-header-helper">
            Best for translating content section by section.<br />
            Enter titles, headings, and paragraphs as separate entries.
          </div>
        </div>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="tm-selector-block">
            <TmLanguageSelector
              selectedTmId={selectedTmId}
              availableTMs={availableTMs}
              onTmChange={onTmChange}
              disabled={submitting || loading}
              idPrefix="section"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="source">
              {columnLabels.source} Text (Source)
            </label>
            <textarea
              id="source"
              className="form-textarea"
              value={source}
              onChange={handleTextChange}
              placeholder={`Enter ${columnLabels.source} text to translate...`}
              required
              disabled={submitting || loading}
              style={{ minHeight: '150px' }}
            />
            <div className={`character-counter ${isNearLimit ? 'warning' : ''} ${isOverLimit ? 'error' : ''}`}>
              {byteLength.toLocaleString()} / {MAX_SOURCE_CHARACTERS.toLocaleString()} characters (UTF-8)
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={submitting || loading || !source.trim() || isOverLimit}
            >
              {submitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Submit Translation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TranslationForm;
